import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Copy, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { mediaUrl } from "@/lib/media";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

function Thumb({ c }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    if (c.media_ids && c.media_ids.length > 0) {
      setSrc(mediaUrl(c.media_ids[0]));
      return;
    }
    api.get(`/creations/${c.id}/image`).then((r) => {
      if (alive && r.data.image_b64) setSrc(`data:image/png;base64,${r.data.image_b64}`);
    }).catch(() => {});
    return () => { alive = false; };
  }, [c]);
  if (!src) return <div className="w-full h-44 bt-gradient grid place-items-center text-4xl text-white/90">✍️</div>;
  return (
    <div className="relative">
      <img src={src} alt={c.title} className="w-full h-44 object-cover" />
      {c.slides?.length > 0 && (
        <span className="absolute top-2 right-2 bg-black/50 text-white text-xs font-bold px-2 py-1 rounded-full">📚 {c.slides.length}</span>
      )}
    </div>
  );
}

export default function Studio() {
  const { t } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/creations"); setItems(data.creations || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => { await api.delete(`/creations/${id}`); setItems(items.filter((i) => i.id !== id)); };
  const copy = async (txt) => { const ok = await copyText(txt); ok ? toast.success(t("copied")) : toast.error(t("err_generic")); };

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-stone-400 text-lg animate-pulse">{t("loading")}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head font-black text-3xl sm:text-4xl mb-8">{t("studio_title")}</h1>
      {items.length === 0 ? (
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-12 text-center">
          <div className="text-5xl mb-4">🎨</div>
          <p className="text-stone-600 text-lg mb-6">{t("studio_empty")}</p>
          <Button onClick={() => nav("/create")} data-testid="studio-start" className="rounded-full bg-primary hover:bg-[#6D28D9] min-h-[56px] px-8 font-bold">
            <Sparkles size={18} className="mr-2" /> {t("start_creating")}
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="creations-grid">
          {items.map((c) => (
            <div key={c.id} className="bg-white rounded-3xl border-2 border-stone-200 overflow-hidden flex flex-col" data-testid={`creation-${c.id}`}>
              <Thumb c={c} />
              <div className="p-5 flex flex-col flex-1">
                <div className="text-xs font-bold uppercase text-primary mb-1">{c.format}</div>
                <h3 className="font-head font-bold text-lg mb-2 line-clamp-1">{c.title || c.idea}</h3>
                <p className="text-stone-600 text-sm line-clamp-3 flex-1">{c.caption}</p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => copy(c.caption)} className="rounded-full flex-1 font-semibold"><Copy size={15} className="mr-1" />{t("copy")}</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-${c.id}`} className="rounded-full text-red-500"><Trash2 size={16} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
